// Web Audio: short tone (mocked "play highlight") + real clip playback + tagged downloads
import { ID3Writer } from 'browser-id3-writer';
import { getClipUrl } from '@/lib/api';
import { resolveHighlightAudioSrc } from '@/data/samples';

let ctx = null;
let activeAudio = null;
let logoBufferPromise = null;

function getCtx() {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

// Play a short pleasant chime — freq varies by theme
export function playTone(themeColorHex = '#20B8F2', durationMs = 500) {
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;

  // Map color -> base frequency
  const freqMap = {
    '#20B8F2': 523.25, // C5 cyan
    '#7EE06D': 659.25, // E5 mint
    '#A78BFA': 587.33, // D5 violet
    '#F2B84C': 698.46, // F5 amber
    '#F2704C': 493.88, // B4 coral
  };
  const base = freqMap[themeColorHex] || 523.25;

  const osc = c.createOscillator();
  const gain = c.createGain();
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 2400;

  osc.type = 'sine';
  osc.frequency.setValueAtTime(base, now);
  osc.frequency.exponentialRampToValueAtTime(base * 1.5, now + durationMs / 1000);

  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(0.18, now + 0.03);
  gain.gain.exponentialRampToValueAtTime(0.001, now + durationMs / 1000);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(c.destination);
  osc.start(now);
  osc.stop(now + durationMs / 1000 + 0.05);
}

export function stopClip() {
  if (activeAudio) {
    activeAudio.pause();
    activeAudio = null;
  }
}

function highlightPlaybackUrl(highlight) {
  const local = resolveHighlightAudioSrc(highlight);
  if (local) return local;
  const clipId = highlight?.clipId || highlight?.clip_id;
  if (clipId) return getClipUrl(clipId);
  return null;
}

function sanitizeFilename(title) {
  return String(title || 'simnopsis-highlight')
    .replace(/[^\w\s\-]+/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 60) || 'simnopsis-highlight';
}

async function loadLogoBuffer() {
  if (!logoBufferPromise) {
    logoBufferPromise = fetch('/simnopsis-logo.png')
      .then((res) => {
        if (!res.ok) throw new Error('Logo fetch failed');
        return res.arrayBuffer();
      })
      .catch((err) => {
        logoBufferPromise = null;
        throw err;
      });
  }
  return logoBufferPromise;
}

function triggerBlobDownload(blob, filename) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
}

function triggerUrlDownload(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Play a demo clipSrc or Simnopsis-HX clip; otherwise fall back to tone. */
export function playHighlight(highlight, themeColorHex = '#20B8F2', { onEnded } = {}) {
  const url = highlightPlaybackUrl(highlight);
  if (!url) {
    playTone(themeColorHex);
    if (onEnded) setTimeout(onEnded, 700);
    return { mode: 'tone' };
  }

  stopClip();
  const audio = new Audio(url);
  activeAudio = audio;
  audio.onended = () => {
    if (activeAudio === audio) activeAudio = null;
    if (onEnded) onEnded();
  };
  audio.onerror = () => {
    if (activeAudio === audio) activeAudio = null;
    playTone(themeColorHex);
    if (onEnded) setTimeout(onEnded, 700);
  };
  audio.play().catch(() => {
    playTone(themeColorHex);
    if (onEnded) setTimeout(onEnded, 700);
  });
  return { mode: 'clip', audio };
}

/**
 * Download a sliced MP3 with SIMNOPSIS ID3 tags (title + logo cover art).
 * Falls back to a plain file download if tagging fails.
 */
export async function downloadHighlight(highlight, { source } = {}) {
  const url = highlightPlaybackUrl(highlight);
  if (!url) return false;

  const title = (highlight?.title || 'SIMNOPSIS Highlight').trim();
  const filename = `${sanitizeFilename(title)}.mp3`;
  const album = source ? `SIMNOPSIS · ${source}` : 'SIMNOPSIS · Highlight Cut';
  const comment = (highlight?.quote || highlight?.excerpt || '').trim();

  try {
    const audioRes = await fetch(url);
    if (!audioRes.ok) throw new Error(`Clip fetch failed (${audioRes.status})`);
    const audioBuffer = await audioRes.arrayBuffer();

    const writer = new ID3Writer(audioBuffer);
    writer
      .setFrame('TIT2', title)
      .setFrame('TPE1', ['SIMNOPSIS'])
      .setFrame('TPE2', 'SIMNOPSIS')
      .setFrame('TALB', album)
      .setFrame('TCON', [highlight?.themeName || 'Speech'])
      .setFrame('TSSE', 'SIMNOPSIS');

    if (comment) {
      writer.setFrame('COMM', {
        description: 'Highlight',
        text: comment,
      });
    }

    try {
      const logoBuffer = await loadLogoBuffer();
      writer.setFrame('APIC', {
        type: 3,
        data: logoBuffer,
        description: 'SIMNOPSIS',
        useUnicodeEncoding: false,
      });
    } catch {
      // Cover art is best-effort; title/artist tags still apply.
    }

    writer.addTag();
    triggerBlobDownload(writer.getBlob(), filename);
    return true;
  } catch {
    triggerUrlDownload(url, filename);
    return true;
  }
}
